import { useEffect, useRef, useState } from 'react';

interface PollingOptions<T> {
  intervalMs: number;
  enabled: boolean;
  // onTransition fires after every successful poll where the new value
  // differs from the previous one according to `compare` (default: deep
  // single-level object compare on the JSON form). Callers use this to push
  // toasts on backend state changes without manually diffing inside their
  // page components.
  onTransition?: (prev: T | null, next: T) => void;
  compare?: (a: T | null, b: T) => boolean;
}

interface PollingResult<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  // refreshing is true during a background tick or a manual refresh() once the
  // first load has landed. Unlike loading it never clears data, so the current
  // rows stay on screen and swap in place — no skeleton flash on an action.
  refreshing: boolean;
  refresh: () => void;
}

function defaultEqual<T>(a: T | null, b: T): boolean {
  if (a === null) return false;
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function usePolling<T>(
  fn: () => Promise<T>,
  opts: PollingOptions<T>,
): PollingResult<T> {
  const { intervalMs, enabled, onTransition, compare } = opts;
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const onTransitionRef = useRef(onTransition);
  onTransitionRef.current = onTransition;
  const compareRef = useRef(compare ?? defaultEqual<T>);
  compareRef.current = compare ?? defaultEqual<T>;
  const dataRef = useRef<T | null>(null);
  dataRef.current = data;

  // fetchNowRef exposes the current polling context's "fetch immediately"
  // function so refresh() can trigger a background refetch WITHOUT re-running
  // the effect (which would blank data and flash a skeleton).
  const fetchNowRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Blank only on a genuine context change (interval/enabled) so a switch
    // never leaves the prior context's rows on screen. A manual refresh() does
    // NOT come through here — it refetches in place via fetchNowRef.
    setData(null);
    setError(null);
    if (!enabled) {
      setLoading(false);
      setRefreshing(false);
      fetchNowRef.current = null;
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // firstLoadDone gates loading (skeleton) to the first fetch of this context;
    // every later fetch (interval tick or manual refresh) is a background
    // refresh that keeps data visible.
    let firstLoadDone = false;

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const run = async () => {
      clearTimer();
      const isInitial = !firstLoadDone;
      if (isInitial) setLoading(true);
      else setRefreshing(true);
      try {
        const next = await fnRef.current();
        if (!cancelled) {
          const prev = dataRef.current;
          const equal = compareRef.current(prev, next);
          setData(next);
          setError(null);
          if (!equal && onTransitionRef.current) {
            onTransitionRef.current(prev, next);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'fetch failed');
        }
      } finally {
        if (!cancelled) {
          firstLoadDone = true;
          if (isInitial) setLoading(false);
          else setRefreshing(false);
          timer = setTimeout(() => void run(), intervalMs);
        }
      }
    };

    fetchNowRef.current = () => void run();
    void run();
    return () => {
      cancelled = true;
      fetchNowRef.current = null;
      clearTimer();
    };
  }, [intervalMs, enabled]);

  return {
    data,
    error,
    loading,
    refreshing,
    refresh: () => fetchNowRef.current?.(),
  };
}
